import { useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import type { ColumnDef } from '@tanstack/react-table';
import { graphql } from '@/gql';
import type { EntranceKind, EntranceLogFilter, EntranceResult } from '@/gql/graphql';
import { useDebouncedUrlField, useUrlState } from '@/hooks/useUrlState';
import { Badge } from '@/components/ui/Badge';
import { DataGrid } from '@/components/ui/DataGrid';
import { FilterBar } from '@/components/ui/FilterBar';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { QueryErrorNotice } from '@/components/ui/QueryErrorNotice';
import { formatDateTime } from '@/lib/format';
import {
  entranceKindLabel,
  entranceResultLabel,
  entranceResultTone,
  refuseReasonLabel,
} from '@/lib/labels';
import { ENTRANCE_KIND_OPTIONS, ENTRANCE_RESULT_OPTIONS, entranceScansUrlSchema } from './entranceScansUrlState';

const EntranceLogsDoc = graphql(/* GraphQL */ `
  query EntranceLogs(
    $filter: EntranceLogFilter
    $page: Int
    $pageSize: Int
  ) {
    entranceLogs(
      filter: $filter
      page: $page
      pageSize: $pageSize
      sort: { field: SCANNED_AT, direction: DESC }
    ) {
      nodes {
        id
        scannedAt
        kind
        result
        guestName
        nightId
        nightTitle
        scannerName
        refuseReason
        refuseNote
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

const NightsForFilterDoc = graphql(/* GraphQL */ `
  query NightsForEntranceFilter {
    nights(page: 1, pageSize: 100, sort: { field: STARTS_AT, direction: DESC }) {
      nodes {
        id
        title
        startsAt
      }
    }
  }
`);

interface Row {
  id: string;
  scannedAt: string;
  kind: EntranceKind;
  result: EntranceResult;
  guestName?: string | null;
  nightTitle?: string | null;
  scannerName?: string | null;
  refuseReason?: string | null;
  refuseNote?: string | null;
}

export function EntranceScansPage() {
  const { state: url, patch, setFilters } = useUrlState(entranceScansUrlSchema);
  const [search, setSearch] = useDebouncedUrlField(url.search, (next) => setFilters({ search: next }));

  const filter = useMemo<EntranceLogFilter>(
    () => ({
      search: url.search.trim() || null,
      result: url.result ? [url.result] : null,
      kind: url.kind ? [url.kind] : null,
      nightIds: url.night ? [url.night] : null,
      scannedAt: url.from || url.to ? { from: url.from || null, to: url.to || null } : null,
    }),
    [url.search, url.result, url.kind, url.night, url.from, url.to],
  );

  const { data, previousData, loading, error } = useQuery(EntranceLogsDoc, {
    variables: { filter, page: url.page, pageSize: url.size },
    errorPolicy: 'all',
  });
  const nightsQuery = useQuery(NightsForFilterDoc, { errorPolicy: 'all' });
  const viewData = data ?? previousData;
  const rows: Row[] = useMemo(
    () => viewData?.entranceLogs.nodes ?? [],
    [viewData],
  );
  const pageInfo = viewData?.entranceLogs.pageInfo;

  const columns = useMemo<ColumnDef<Row>[]>(
    () => [
      {
        id: 'time',
        header: 'Time',
        cell: ({ row }) => formatDateTime(row.original.scannedAt),
      },
      {
        id: 'guest',
        header: 'Guest',
        cell: ({ row }) => row.original.guestName || '—',
      },
      {
        id: 'kind',
        header: 'Type',
        cell: ({ row }) => entranceKindLabel(row.original.kind),
      },
      {
        id: 'night',
        header: 'Night',
        cell: ({ row }) => row.original.nightTitle || '—',
      },
      {
        id: 'scanner',
        header: 'Scanner',
        cell: ({ row }) => row.original.scannerName || '—',
      },
      {
        id: 'result',
        header: 'Result',
        cell: ({ row }) => (
          <Badge tone={entranceResultTone(row.original.result)}>
            {entranceResultLabel(row.original.result)}
          </Badge>
        ),
      },
      {
        id: 'reason',
        header: 'Reason',
        cell: ({ row }) => (
          <div className="min-w-32 whitespace-normal">
            {row.original.refuseReason
              ? refuseReasonLabel(row.original.refuseReason as never)
              : '—'}
          </div>
        ),
      },
      {
        id: 'note',
        header: 'Free text',
        cell: ({ row }) => (
          <div className="max-w-xs min-w-40 whitespace-normal">
            {row.original.refuseNote || '—'}
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4" id="admin.entrance">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Entrance scans
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Every door scan: admitted, refused, blocked and invalid attempts. This
          log&apos;s PII is not sent to Customer.io.
        </p>
      </div>

      {error ? <QueryErrorNotice error={error} /> : null}

      <FilterBar
        search={{
          id: 'admin.entrance.search',
          value: search,
          onChange: setSearch,
          placeholder: 'Guest name',
        }}
        primary={
          <div className="flex flex-wrap gap-2">
            <Select
              id="admin.entrance.filter.result"
              value={url.result}
              onChange={(e) => setFilters({ result: e.target.value as '' | EntranceResult })}
              options={ENTRANCE_RESULT_OPTIONS}
            />
            <Select
              id="admin.entrance.filter.kind"
              value={url.kind}
              onChange={(e) => setFilters({ kind: e.target.value as '' | EntranceKind })}
              options={ENTRANCE_KIND_OPTIONS}
            />
            <Select
              id="admin.entrance.filter.night"
              value={url.night}
              onChange={(e) => setFilters({ night: e.target.value })}
              options={[
                { value: '', label: 'Any night' },
                ...(nightsQuery.data?.nights.nodes ?? []).map((p) => ({
                  value: p.id,
                  label: p.title,
                })),
              ]}
            />
            <Input
              id="admin.entrance.filter.from"
              type="date"
              value={url.from}
              onChange={(e) => setFilters({ from: e.target.value })}
            />
            <Input
              id="admin.entrance.filter.to"
              type="date"
              value={url.to}
              onChange={(e) => setFilters({ to: e.target.value })}
            />
          </div>
        }
      />

      <DataGrid<Row>
        data={rows}
        columns={columns}
        loading={loading && !viewData}
        rowId={(row) => row.id}
        rowDomId={(row) => `admin.entrance.row.${row.id}`}
        pagination={{
          page: pageInfo?.page ?? url.page,
          pageSize: pageInfo?.pageSize ?? url.size,
          totalRows: pageInfo?.totalRows ?? 0,
          totalPages: pageInfo?.totalPages ?? 1,
          onPageChange: (next) => patch({ page: next }),
          onPageSizeChange: (next) => setFilters({ size: next }),
        }}
      />
    </div>
  );
}
